import React, { useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useSharedStyles from './theme/pbdqShared.styles';
import { pbdqFont } from './theme/pbdqTokens';
import { sdsColors, sdsRadius, sdsBtnBase, SDS_RAILS } from './theme/solutionsDesignSystem';
import pbdqRoutes from './pbdqRoutes';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import { useAgentChat } from './components/PbdqAgentChatContext';
import PbdqGradientStripe from './components/PbdqGradientStripe.comp';
import PbdqArrowRightIcon from './components/PbdqArrowRightIcon.comp';
import PbdqPricingSolutionCard from './components/PbdqPricingSolutionCard.comp';
import PbdqImageLightboxWrapper from './components/PbdqImageLightboxWrapper.comp';

import PricingHeroTeam from '../../assets/images/PoweredByDQ/PricingHeroTeam.jpg';
import PricingDQWorkspace0 from '../../assets/images/PoweredByDQ/PricingDQWorkspace0.png';
import PricingDQWorkspace1 from '../../assets/images/PoweredByDQ/PricingDQWorkspace1.png';
import PricingDQWorkspace2 from '../../assets/images/PoweredByDQ/PricingDQWorkspace2.png';
import PricingDQWorkspace3 from '../../assets/images/PoweredByDQ/PricingDQWorkspace3.png';
import PricingDQWorkspace4 from '../../assets/images/PoweredByDQ/PricingDQWorkspace4.png';
import PricingDQWorkspace5 from '../../assets/images/PoweredByDQ/PricingDQWorkspace5.png';
import PricingDQWorkspace6 from '../../assets/images/PoweredByDQ/PricingDQWorkspace6.png';

const useStyles = makeStyles(() => ({
    accentText: { color: sdsColors.accent },

    // Hero Section (Mockup / Pricing)
    heroSection: {
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220, 231, 243, 0) 60%), linear-gradient(180deg, #F6F8FB 0%, #FFFFFF 100%)',
    },
    heroInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(40px, 5vw, 64px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '32px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    heroContent: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 22,
        alignContent: 'start',
    },
    heroTitle: {
        fontSize: 'clamp(36px, 4.2vw, 60px)',
        lineHeight: 1.04,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '16ch',
        fontFamily: pbdqFont,
        color: '#1B2430',
    },
    heroTitleHighlight: {
        color: '#ED6628',
    },
    heroLead: {
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '62ch',
        fontFamily: pbdqFont,
    },
    heroBtns: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
    },
    heroBtnPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        fontFamily: pbdqFont,
        whiteSpace: 'nowrap',
        border: 'none',
        cursor: 'pointer',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    heroBtnSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#1B2430',
        border: '1px solid #D5D9E0',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 600,
        fontFamily: pbdqFont,
        whiteSpace: 'nowrap',
        transition: 'all 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            color: '#ED6628',
            borderColor: '#ED6628',
            textDecoration: 'none',
        },
    },
    heroArt: {
        position: 'relative',
        flex: '1 1 440px',
        minWidth: 0,
        minHeight: 600,
        boxSizing: 'border-box',
        '@media (max-width: 820px)': {
            minHeight: 0,
            maxWidth: 'none',
        },
    },
    heroPhoto: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 170,
        left: '14%',
        borderRadius: 28,
        overflow: 'hidden',
        background: '#172939',
        '@media (max-width: 820px)': {
            position: 'relative',
            inset: 'auto',
            height: 240,
            margin: 0,
            left: 0,
        },
    },
    heroPhotoImg: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: '50% 40%',
        display: 'block',
        filter: 'saturate(0.78) contrast(1.04)',
    },
    heroPhotoGrad: {
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(160deg, rgba(23,41,57,0.55) 0%, rgba(23,41,57,0.18) 45%, rgba(23,41,57,0) 70%), linear-gradient(0deg, rgba(23,41,57,0.45) 0%, rgba(23,41,57,0) 40%)',
    },
    heroOrbit1: {
        position: 'absolute',
        inset: 'auto -10% -30% 30%',
        height: '70%',
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.35)',
        pointerEvents: 'none',
    },
    heroOrbit2: {
        position: 'absolute',
        inset: '-20% -20% auto 45%',
        height: '70%',
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        pointerEvents: 'none',
    },
    heroCard: {
        position: 'absolute',
        left: 0,
        right: '12%',
        bottom: 0,
        padding: 'clamp(22px, 2.6vw, 32px)',
        borderRadius: 24,
        background: '#FFFFFF',
        border: '1px solid rgba(23,41,57,0.06)',
        boxShadow: '0 30px 80px rgba(23,41,57,0.18), 0 2px 6px rgba(23,41,57,0.06)',
        display: 'grid',
        gap: 14,
        boxSizing: 'border-box',
        '@media (max-width: 820px)': {
            position: 'relative',
            inset: 'auto',
            margin: '-40px 8px 0',
            maxWidth: 'none',
            right: 0,
        },
    },
    heroCardLabel: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#6B7280',
        fontFamily: pbdqFont,
    },
    heroCardList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 10,
    },
    heroCardItem: {
        display: 'grid',
        gridTemplateColumns: '18px 1fr',
        gap: 10,
        fontSize: 14.5,
        lineHeight: 1.5,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    heroCardCheck: {
        marginTop: 2,
        flexShrink: 0,
    },

    sectionChip: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 14px',
        borderRadius: sdsRadius.pill,
        background: sdsColors.tint,
        color: sdsColors.accentDark,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        marginBottom: 16,
    },
    sectionChipSurface: {
        background: sdsColors.surface,
        border: `1px solid ${sdsColors.borderWarm}`,
    },

    // Generic section scaffolding
    section: {
        padding: '48px 0 56px',
        display: 'flex',
        flexDirection: 'column',
        gap: 48,
    },
    sectionAlt: {
        background: sdsColors.tint,
    },
    sectionWhite: {
        background: sdsColors.surface,
    },
    sectionHeader: {
        textAlign: 'center',
        margin: '0 auto',
    },
    h2: {
        margin: '0 auto',
        fontSize: 'clamp(28px, 3vw, 36px)',
        fontWeight: 700,
        lineHeight: 1.15,
        color: sdsColors.text,
    },
    lead: {
        margin: '12px auto 0',
        maxWidth: 900,
        fontSize: 18,
        lineHeight: 1.55,
        color: sdsColors.textMuted,
    },

    // Why become a DQ Pro section
    whySection: {
        background: '#FFFFFF',
    },
    whyInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    whyEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
        fontFamily: pbdqFont,
    },
    whyHeader: {
        display: 'grid',
        gap: 16,
    },
    whyTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        maxWidth: 'none',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    whyLead: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        maxWidth: 'none',
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    marketGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 36,
    },
    marketCard: {
        padding: 'clamp(22px, 2.6vw, 32px)',
        borderRadius: 20,
        display: 'grid',
        gap: 10,
        alignContent: 'start',
        boxSizing: 'border-box',
    },
    marketIndex: {
        fontSize: 13,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: pbdqFont,
    },
    marketCardTitle: {
        fontSize: 'clamp(20px, 1.9vw, 24px)',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    marketCardBody: {
        fontSize: 14.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },

    // Infrastructure banner
    infraBanner: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(24px, 3vw, 48px)',
        alignItems: 'center',
        marginTop: 'clamp(16px, 2vw, 24px)',
        padding: 'clamp(24px, 3vw, 40px)',
        borderRadius: 24,
        background: '#172939',
        color: '#FFFFFF',
        boxSizing: 'border-box',
    },
    infraContent: {
        flex: '1 1 380px',
        minWidth: 0,
        display: 'grid',
        gap: 12,
    },
    infraTitle: {
        fontSize: 'clamp(22px, 2.3vw, 30px)',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#FFFFFF',
        fontFamily: pbdqFont,
    },
    infraText: {
        fontSize: 15,
        lineHeight: 1.6,
        margin: 0,
        color: '#D9E3EE',
        fontFamily: pbdqFont,
    },
    infraCardsWrap: {
        flex: '1 1 380px',
        minWidth: 0,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
    },
    infraPillCard: {
        flex: '1 1 150px',
        padding: '16px 18px',
        borderRadius: 16,
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.14)',
        display: 'grid',
        gap: 4,
        boxSizing: 'border-box',
    },
    infraOperator: {
        fontSize: 22,
        fontWeight: 800,
        color: '#8AA3BC',
        fontFamily: pbdqFont,
    },
    infraPillCardCycle: {
        flex: '1 1 170px',
        padding: '16px 18px',
        borderRadius: 16,
        background: '#ED6628',
        display: 'grid',
        gap: 4,
        boxSizing: 'border-box',
    },
    infraCardTitle: {
        fontSize: 18,
        fontWeight: 800,
        color: '#FFFFFF',
        fontFamily: pbdqFont,
    },
    infraCardSub: {
        fontSize: 13,
        lineHeight: 1.4,
        color: '#B7C8DA',
        fontFamily: pbdqFont,
    },
    infraCardSubWhite: {
        fontSize: 13,
        lineHeight: 1.4,
        color: '#FFFFFF',
        fontFamily: pbdqFont,
    },

    // Workspace section
    workspaceSection: {
        background: '#F4F6F9',
    },
    workspaceInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    workspaceHeaderWrap: {
        display: 'grid',
    },
    workspaceEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
        fontFamily: pbdqFont,
    },
    workspaceHeader: {
        display: 'grid',
        gap: 16,
    },
    workspaceTitle: {
        fontSize: 'clamp(28px, 3.4vw, 46px)',
        lineHeight: 1.06,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        maxWidth: 'none',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    workspaceTitleHighlight: {
        color: '#ED6628',
    },
    workspaceLead: {
        fontSize: 16,
        lineHeight: 1.55,
        margin: 0,
        maxWidth: 'none',
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    workspaceFigure: {
        margin: '36px 0 0',
        padding: 10,
        borderRadius: 24,
        background: '#FFFFFF',
        border: '1px solid #E6E9EE',
        boxShadow: '0 30px 70px rgba(23,41,57,0.12)',
        boxSizing: 'border-box',
    },
    workspaceLightboxWrap: {
        width: '100%',
        display: 'block',
        borderRadius: 16,
    },
    workspaceMainImg: {
        width: '100%',
        borderRadius: 16,
        display: 'block',
    },
    workspaceGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 'clamp(20px, 2.4vw, 28px)',
    },
    workspaceCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: 'clamp(20px, 2.4vw, 28px)',
        border: '1px solid #E6E9EE',
        borderRadius: 20,
        background: '#FFFFFF',
        boxSizing: 'border-box',
    },
    workspaceBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: pbdqFont,
    },
    workspaceCardLightboxWrap: {
        width: '100%',
        aspectRatio: '5/2',
        display: 'block',
        borderRadius: 10,
        overflow: 'hidden',
    },
    workspaceCardShot: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'top left',
        borderRadius: 10,
        border: '1px solid #EEF1F4',
        display: 'block',
    },
    workspaceCardTitle: {
        fontSize: 18,
        lineHeight: 1.25,
        letterSpacing: '-0.015em',
        fontWeight: 700,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    workspaceCardBody: {
        fontSize: 14.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    workspaceFooterNote: {
        fontSize: 16,
        lineHeight: 1.5,
        fontWeight: 600,
        margin: '24px 0 0',
        color: '#2A4057',
        textAlign: 'center',
        fontFamily: pbdqFont,
    },

    // How the DQ Pro model works section
    modelSection: {
        background: '#FFFFFF',
    },
    modelInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    modelContainer: {
        padding: 'clamp(24px, 3vw, 44px)',
        borderRadius: 24,
        background: '#F4F6F9',
        boxSizing: 'border-box',
    },
    modelEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
        fontFamily: pbdqFont,
    },
    modelTitle: {
        fontSize: 'clamp(26px, 3vw, 40px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        maxWidth: 'none',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    cycleGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
        gap: 12,
        marginTop: 32,
    },
    cycleCard: {
        position: 'relative',
        padding: 18,
        borderRadius: 16,
        display: 'grid',
        gap: 8,
        alignContent: 'start',
        boxSizing: 'border-box',
    },
    cycleCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    cycleBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        borderRadius: '50%',
        fontSize: 12,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: pbdqFont,
    },
    cycleArrow: {
        opacity: 0.5,
        fontFamily: pbdqFont,
    },
    cycleCardTitle: {
        fontSize: 17,
        fontWeight: 800,
        letterSpacing: '-0.01em',
        margin: 0,
        fontFamily: pbdqFont,
    },
    cycleCardBody: {
        fontSize: 13.5,
        lineHeight: 1.5,
        margin: 0,
        opacity: 0.85,
        fontFamily: pbdqFont,
    },
    cycleFooterNote: {
        fontSize: 14.5,
        lineHeight: 1.55,
        margin: '20px 0 0',
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    cycleRepeatLabel: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginRight: 10,
        fontFamily: pbdqFont,
    },

    // What you can take to market section
    solutionsSection: {
        background: '#FFF8F4',
    },
    solutionsInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    solutionsEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
        fontFamily: pbdqFont,
    },
    solutionsHeader: {
        display: 'grid',
        gap: 16,
    },
    solutionsTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        maxWidth: 'none',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    solutionsLeadWrap: {
        display: 'grid',
        gap: 8,
        maxWidth: 'none',
    },
    solutionsLead: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    solutionsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 36,
    },
    solutionCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 'clamp(20px, 2.4vw, 28px)',
        border: '1px solid #E6E9EE',
        borderRadius: 20,
        background: '#FFFFFF',
        boxSizing: 'border-box',
    },
    solutionCategory: {
        fontSize: 12,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#6B7280',
        fontFamily: pbdqFont,
    },
    solutionCardTitle: {
        fontSize: 19,
        lineHeight: 1.25,
        letterSpacing: '-0.015em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    solutionCardDesc: {
        fontSize: 14.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    solutionIdealClients: {
        fontSize: 13.5,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    solutionIdealClientsLabel: {
        color: '#1B2430',
        fontWeight: 700,
    },
    solutionRevenueBox: {
        display: 'grid',
        gap: 6,
        padding: '14px 16px',
        borderRadius: 14,
        background: '#F8F9FB',
        marginTop: 'auto',
    },
    solutionRevenueLabel: {
        fontSize: 12,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#6B7280',
        fontFamily: pbdqFont,
    },
    solutionRevenueItem: {
        display: 'grid',
        gridTemplateColumns: '14px 1fr',
        gap: 8,
        fontSize: 13.5,
        lineHeight: 1.45,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    solutionRevenueDot: {
        color: '#ED6628',
        fontWeight: 800,
    },
    solutionLink: {
        fontSize: 14,
        fontWeight: 700,
        color: '#A63C0C',
        textDecoration: 'none',
        fontFamily: pbdqFont,
        display: 'inline-flex',
        alignItems: 'center',
        marginTop: 4,
        '&:hover': {
            color: '#ED6628',
            textDecoration: 'none',
        },
    },
    agentCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 'clamp(20px, 2.4vw, 28px)',
        border: '1px solid #E6E9EE',
        borderRadius: 20,
        background: '#FFFFFF',
        boxSizing: 'border-box',
    },
    agentIcon: {
        width: 48,
        height: 48,
        borderRadius: sdsRadius.pill,
        background: sdsColors.surface,
        color: sdsColors.accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    agentTitle: {
        margin: 0,
        fontWeight: 700,
        fontSize: 18,
        color: sdsColors.text,
    },
    agentDesc: {
        margin: 0,
        fontSize: 14,
        lineHeight: 1.5,
        color: sdsColors.textMuted,
        maxWidth: '32ch',
    },

    // Commercial model section
    commercialSection: {
        background: '#FFFFFF',
    },
    commercialInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    commercialEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
        fontFamily: pbdqFont,
    },
    commercialHeader: {
        display: 'grid',
        gap: 16,
    },
    commercialTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        maxWidth: 'none',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    commercialLead: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        maxWidth: 'none',
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    commercialGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: 'clamp(12px, 1.4vw, 16px)',
        marginTop: 36,
    },
    commercialCard: {
        padding: 'clamp(20px, 2.2vw, 26px)',
        borderRadius: 20,
        display: 'grid',
        gap: 8,
        alignContent: 'start',
        boxSizing: 'border-box',
    },
    commercialIndex: {
        fontSize: 13,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: pbdqFont,
    },
    commercialCardTitle: {
        fontSize: 17,
        lineHeight: 1.25,
        letterSpacing: '-0.015em',
        fontWeight: 800,
        margin: '4px 0 0',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    commercialCardDesc: {
        fontSize: 14,
        lineHeight: 1.55,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    commercialFooterNote: {
        fontSize: 15,
        lineHeight: 1.55,
        margin: '20px 0 0',
        color: '#5B6470',
        fontFamily: pbdqFont,
    },

    // Calculator
    // DQ Pro opportunity calculator
    calcSection: {
        background: '#F4F6F9',
        width: '100%',
    },
    calcInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    calcEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
    },
    calcHeaderRow: {
        display: 'grid',
        gap: 16,
        maxWidth: 'none',
    },
    calcHeading: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    calcSubtext: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    calcBody: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 36,
        alignItems: 'flex-start',
    },
    calcFormCol: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 'clamp(14px, 1.6vw, 20px)',
    },
    calcCard: {
        padding: 'clamp(20px, 2.4vw, 28px)',
        border: '1px solid #D9DEE5',
        borderRadius: 20,
        background: '#FFFFFF',
        boxShadow: '0 4px 16px rgba(23, 41, 57, 0.04)',
        display: 'grid',
        gap: 14,
        boxSizing: 'border-box',
    },
    calcCardTitle: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        margin: 0,
    },
    calcInputGrid2: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        gap: 12,
    },
    calcInputGrid3: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))',
        gap: 12,
    },
    calcFieldLabel: {
        display: 'grid',
        gap: 6,
        fontSize: 13,
        fontWeight: 600,
        color: '#5B6470',
    },
    calcSelectControl: {
        height: 46,
        padding: '0 40px 0 14px',
        border: '1px solid #D5D9E0',
        borderRadius: 12,
        fontFamily: 'inherit',
        fontSize: 14.5,
        color: '#1B2430',
        background: '#FFFFFF',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231B2430' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 14px center',
        backgroundSize: '16px 16px',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'pointer',
    },
    calcInputControl: {
        height: 46,
        padding: '0 14px',
        border: '1px solid #D5D9E0',
        borderRadius: 12,
        fontFamily: 'inherit',
        fontSize: 15,
        color: '#1B2430',
        background: '#FFFFFF',
        fontVariantNumeric: 'tabular-nums',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
    },
    calcHelpText: {
        fontSize: 12,
        fontWeight: 500,
        color: '#6B7280',
    },
    calcFootnote: {
        fontSize: 12.5,
        margin: 0,
        color: '#6B7280',
        lineHeight: 1.5,
    },
    calcComputedDisplay: {
        display: 'flex',
        alignItems: 'center',
        height: 46,
        padding: '0 14px',
        borderRadius: 12,
        background: '#F4F6F9',
        fontSize: 18,
        fontWeight: 800,
        color: '#1B2430',
        fontVariantNumeric: 'tabular-nums',
        boxSizing: 'border-box',
    },
    calcSubSection: {
        display: 'grid',
        gap: 12,
        paddingTop: 14,
        borderTop: '1px solid #EEF1F4',
    },
    calcSubSectionTitle: {
        fontSize: 13,
        fontWeight: 700,
        color: '#1B2430',
    },
    calcPriceBoxGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
        gap: 12,
    },
    calcPriceBoxDefault: {
        padding: '14px 16px',
        borderRadius: 14,
        background: '#F4F6F9',
        display: 'grid',
        gap: 4,
    },
    calcPriceBoxCustom: {
        padding: '14px 16px',
        borderRadius: 14,
        background: '#FDECE3',
        display: 'grid',
        gap: 4,
    },
    calcPriceBoxLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: '#6B7280',
    },
    calcPriceBoxLabelCustom: {
        fontSize: 12,
        fontWeight: 600,
        color: '#A63C0C',
    },
    calcPriceBoxValue: {
        fontSize: 22,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        fontVariantNumeric: 'tabular-nums',
        color: '#1B2430',
    },
    calcPriceBoxValueCustom: {
        fontSize: 22,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: '#A63C0C',
        fontVariantNumeric: 'tabular-nums',
    },
    calcPriceBoxUnit: {
        fontSize: 13,
        fontWeight: 500,
        color: '#6B7280',
    },
    calcPriceBoxSub: {
        fontSize: 12,
        color: '#6B7280',
        lineHeight: 1.45,
    },
    calcPriceBoxSubCustom: {
        fontSize: 12,
        color: '#A63C0C',
        lineHeight: 1.45,
    },
    calcRangeRow: {
        display: 'grid',
        gap: 8,
        fontSize: 13,
        fontWeight: 600,
        color: '#5B6470',
    },
    calcRangeSlider: {
        width: '100%',
        accentColor: '#ED6628',
        cursor: 'pointer',
    },
    calcRangeLabelsBetween: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 12,
        fontWeight: 500,
        color: '#6B7280',
    },
    calcAsideCol: {
        flex: '1 1 320px',
        minWidth: 0,
        maxWidth: 460,
        position: 'sticky',
        top: 20,
        display: 'grid',
        gap: 'clamp(14px, 1.6vw, 20px)',
    },
    calcDarkOpportunityCard: {
        padding: 'clamp(22px, 2.6vw, 32px)',
        borderRadius: 24,
        background: '#172939',
        color: '#FFFFFF',
        display: 'grid',
        gap: 18,
        boxSizing: 'border-box',
    },
    calcDarkCardEyebrow: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#8AA3BC',
    },
    calcDarkTotalValue: {
        fontSize: 'clamp(36px, 3.6vw, 52px)',
        lineHeight: 1,
        fontWeight: 800,
        letterSpacing: '-0.035em',
        margin: 0,
        fontVariantNumeric: 'tabular-nums',
        color: '#FFFFFF',
    },
    calcDarkTotalTitle: {
        fontSize: 14,
        fontWeight: 700,
        margin: '8px 0 2px',
        color: '#FFFFFF',
    },
    calcDarkTotalDesc: {
        fontSize: 13,
        lineHeight: 1.5,
        margin: 0,
        color: '#B7C8DA',
    },
    calcRevBarWrap: {
        display: 'flex',
        height: 10,
        borderRadius: 999,
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.1)',
    },
    calcRevBarSol: {
        background: '#ED6628',
    },
    calcRevBarSvc: {
        background: '#8AA3BC',
    },
    calcRevLegend: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 13,
        fontVariantNumeric: 'tabular-nums',
        color: '#FFFFFF',
    },
    calcProfitBox: {
        padding: '16px 18px',
        borderRadius: 16,
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.14)',
    },
    calcProfitValText: {
        fontSize: 'clamp(26px, 2.6vw, 34px)',
        lineHeight: 1,
        fontWeight: 800,
        letterSpacing: '-0.03em',
        margin: 0,
        color: '#FFFFFF',
        fontVariantNumeric: 'tabular-nums',
    },
    calcSummaryCard: {
        padding: 'clamp(20px, 2.4vw, 28px)',
        border: '1px solid #D9DEE5',
        borderRadius: 20,
        background: '#FFFFFF',
        boxShadow: '0 4px 16px rgba(23, 41, 57, 0.04)',
        display: 'grid',
        gap: 12,
        boxSizing: 'border-box',
    },
    calcSummaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: '9px 0',
        borderBottom: '1px solid #EEF1F4',
        fontSize: 13.5,
    },
    calcSummaryKeyText: {
        color: '#6B7280',
    },
    calcSummaryValText: {
        fontWeight: 700,
        textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
        color: '#1B2430',
    },
    calcResetButton: {
        justifySelf: 'start',
        height: 42,
        padding: '0 18px',
        borderRadius: 999,
        border: '1px solid #D5D9E0',
        background: '#FFFFFF',
        color: '#1B2430',
        fontFamily: 'inherit',
        fontSize: 13.5,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': {
            background: '#F4F6F9',
            borderColor: '#BFC5CD',
        },
    },
    calcDisclaimerText: {
        fontSize: 12,
        lineHeight: 1.55,
        margin: 0,
        color: '#6B7280',
    },

    // How to start (#apply)
    applySection: {
        background: '#F4F6F9',
        width: '100%',
    },
    applyInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    applyEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
    },
    applyHeading: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    applyGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))',
        gap: 'clamp(12px, 1.4vw, 16px)',
        marginTop: 36,
    },
    applyCard: {
        padding: 'clamp(20px, 2.4vw, 28px)',
        border: '1px solid #E6E9EE',
        borderRadius: 20,
        background: '#FFFFFF',
        display: 'grid',
        gap: 10,
        alignContent: 'start',
        boxSizing: 'border-box',
    },
    applyBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: '#FDECE3',
        color: '#A63C0C',
        fontSize: 13,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
    },
    applyTitle: {
        fontSize: 18,
        lineHeight: 1.25,
        letterSpacing: '-0.015em',
        fontWeight: 700,
        margin: '6px 0 0',
        color: '#1B2430',
    },
    applyDesc: {
        fontSize: 14.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    applyBanner: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px 40px',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 'clamp(16px, 2vw, 24px)',
        padding: 'clamp(22px, 2.6vw, 32px)',
        borderRadius: 24,
        background: '#FFFFFF',
        boxSizing: 'border-box',
    },
    applyBannerTitle: {
        fontSize: 'clamp(20px, 2vw, 26px)',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
    },
    applyBannerDesc: {
        fontSize: 15,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
    },
    applyBannerBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        border: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
        },
    },

    // Bottom Dark CTA Section
    bottomCtaSection: {
        background: '#172939',
        color: '#FFFFFF',
        width: '100%',
    },
    bottomCtaInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px 48px',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    bottomCtaHeading: {
        fontSize: 'clamp(28px, 3.4vw, 48px)',
        lineHeight: 1.06,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '22ch',
    },
    bottomCtaHeadingWhite: {
        display: 'block',
        color: '#FFFFFF',
    },
    bottomCtaHeadingSub: {
        display: 'block',
        color: '#8AA3BC',
    },
    bottomCtaActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'center',
    },
    bottomCtaBtnPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        border: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textDecoration: 'none',
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
        },
    },
    bottomCtaBtnSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: 'transparent',
        color: '#FFFFFF !important',
        border: '1px solid #5D7A96',
        fontSize: 15,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textDecoration: 'none !important',
        transition: 'all 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.08)',
            borderColor: '#FFFFFF',
            color: '#FFFFFF !important',
            textDecoration: 'none !important',
        },
    },
    btnPrimary: {
        ...sdsBtnBase,
        background: sdsColors.accent,
        color: '#fff',
        '&:hover': {
            background: sdsColors.accentDark,
            color: '#fff',
            textDecoration: 'none',
        },
    },
    btnOutline: {
        ...sdsBtnBase,
        background: sdsColors.surface,
        color: sdsColors.accentDark,
        borderColor: sdsColors.accent,
        '&:hover': {
            background: sdsColors.imagePlaceholder,
            color: sdsColors.accentDark,
            textDecoration: 'none',
        },
    },
    btnOnDark: {
        ...sdsBtnBase,
        background: '#fff',
        color: sdsColors.accentDark,
        '&:hover': {
            background: sdsColors.bg,
            color: sdsColors.accentDark,
            textDecoration: 'none',
        },
    },
    btnOutlineOnDark: {
        ...sdsBtnBase,
        background: 'transparent',
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.7)',
        '&:hover': {
            background: 'rgba(255,255,255,0.1)',
            color: '#fff',
            textDecoration: 'none',
        },
    },
}));

const HERO_BULLETS = [
    'Access wholesale pricing that improves with committed volume',
    'Manage your clients and deployments from one partner workspace',
    'Use assessment data to identify each client’s priority needs',
    'Create tailored programs with LearnCreator',
    'Set your own client packages and pricing',
    'Add implementation, training, localization and support services',
    'Own your client relationships and retain your margin',
];

const FIT_CRITERIA = [
    'You already work with governments, schools or companies in your market',
    'You want to sell measurable AI-digital literacy, online-safety or workforce-readiness solutions',
    'You have, or can build, local delivery, training or consulting capacity',
    'You can commit to an annual license or program volume',
    'You want a recurring, expandable client relationship — not a one-off sale',
    'You’d rather focus on clients and delivery than build assessment and learning technology yourself',
];

// Why become a DQ Pro — Market cards
const WHY_REASONS = [
    {
        index: '01',
        title: 'Demand is accelerating',
        desc: 'Governments, schools and companies must respond to new AI, online-safety and digital-skills expectations. They need practical solutions that translate policy into measurable action.',
        tint: '#E1F0F9',
        ink: '#1F5F86',
    },
    {
        index: '02',
        title: 'Buyers need more than content',
        desc: 'A course alone cannot show where capability gaps exist, whether learning is relevant or what improvement it produced. Clients increasingly need an integrated cycle of assessment, targeted learning and measurable outcomes.',
        tint: '#EDF2D5',
        ink: '#4E6A12',
    },
    {
        index: '03',
        title: 'Building the complete system is difficult',
        desc: 'Developing standards-aligned assessments, learning tools, dashboards, reporting and program-management infrastructure requires significant expertise, investment and time.',
        tint: '#FAEDDA',
        ink: '#8A5A10',
    },
];

const WORKSPACE_STEPS = [
    {
        index: '01',
        title: 'Bring clients into Powered by DQ',
        desc: 'Create client organizations, structure their schools, departments or business units, and invite the appropriate administrators, educators and program managers.',
        shot: PricingDQWorkspace1,
        hasShot: true,
        tint: '#E1F0F9',
        ink: '#1F5F86',
    },
    {
        index: '02',
        title: 'Configure and launch solutions',
        desc: 'Assign licenses, activate assessments and learning solutions, distribute programs and monitor participation across every client deployment.',
        shot: PricingDQWorkspace2,
        hasShot: true,
        tint: '#EDF2D5',
        ink: '#4E6A12',
    },
    {
        index: '03',
        title: 'See where each client needs support',
        desc: 'Review client-level dashboards showing readiness, capability gaps, risk exposure and priority areas. Use the evidence to recommend the next program, service or expansion.',
        shot: PricingDQWorkspace3,
        hasShot: true,
        tint: '#FAEDDA',
        ink: '#8A5A10',
    },
    {
        index: '04',
        title: 'Create tailored programs with LearnCreator',
        desc: 'Turn the gaps identified in assessment data into standards-aligned learning programs tailored to the client’s audience, curriculum, industry, language and delivery format.',
        shot: PricingDQWorkspace4,
        hasShot: true,
        tint: '#FBEDE4',
        ink: '#A63C0C',
    },
    {
        index: '05',
        title: 'Track delivery and demonstrate results',
        desc: 'Monitor completion, compare results and measure improvement over time. Give clients clear evidence of progress through organizational dashboards and reports.',
        shot: PricingDQWorkspace5,
        hasShot: true,
        tint: '#F6E5F0',
        ink: '#8E1F55',
    },
    {
        index: '06',
        title: 'Identify opportunities to expand',
        desc: 'See license use, program activity and solution adoption across your portfolio. Identify when a client is ready to add learners, programs, creation capacity, schools, departments or new solutions.',
        shot: PricingDQWorkspace6,
        hasShot: true,
        tint: '#E1F0F9',
        ink: '#1F5F86',
    },
];

// How the DQ Pro model works — 6 cycle steps
const CYCLE_STEPS = [
    {
        n: '1',
        title: 'Measure',
        desc: 'Deploy Powered by DQ assessments to establish a trusted baseline and identify the client’s most important capability gaps.',
    },
    {
        n: '2',
        title: 'Design',
        desc: 'Use the data to define the right intervention, target audience, learning objectives and delivery model.',
    },
    {
        n: '3',
        title: 'Create',
        desc: 'Use LearnCreator to develop or adapt standards-aligned programs for the client’s specific needs and context.',
    },
    {
        n: '4',
        title: 'Deliver',
        desc: 'Launch the program across the client’s schools, teams or population through the Powered by DQ workspace.',
    },
    {
        n: '5',
        title: 'Re-measure',
        desc: 'Track participation and measure progress against the original baseline, giving the client evidence of what changed.',
    },
    {
        n: '6',
        title: 'Expand',
        desc: 'Use the results to recommend follow-up programs, additional populations or new Powered by DQ solutions.',
    },
].map((c, i, arr) => {
    const isDark = i === 0 || i === arr.length - 1;
    return {
        ...c,
        fill: isDark ? '#172939' : '#FFFFFF',
        ink: isDark ? '#FFFFFF' : '#1B2430',
        badgeBg: isDark ? '#ED6628' : '#EEF3F8',
        badgeInk: isDark ? '#FFFFFF' : '#2A4057',
        arrow: i < arr.length - 1,
    };
});

const SOLUTION_CARDS = [
    {
        category: 'COS Children Assessment · P0',
        title: 'Establish children’s online safety baseline.',
        description: 'Measure children’s digital-citizenship readiness, cyber-risk exposure and priority support needs across schools, school networks or national populations.',
        idealClients: 'Schools · school networks · education authorities · governments · foundations',
        bullets: [
            'Assessment deployment and participant access',
            'Implementation, reporting and stakeholder briefings',
            'Research, benchmarking and advisory services',
        ],
        to: pbdqRoutes.cosAssessments,
    },
    {
        category: 'School COS Readiness · P1A',
        title: 'Create and manage programs centrally.',
        description: 'Combine baseline assessment, centrally created learning, delivery and impact tracking—with your team creating consistent programs and distributing them across the client’s schools.',
        idealClients: 'School networks · education authorities · government programs · large-scale initiatives',
        bullets: [
            'Annual learner deployments',
            'Central program creation and localization',
            'School implementation, management and reporting',
        ],
        to: pbdqRoutes.schoolCosReadiness,
    },
    {
        category: 'School COS Readiness · P1B',
        title: 'Equip schools to create their own programs.',
        description: 'Provide the same continuous-improvement cycle while enabling the client’s educators to create and adapt programs through LearnCreator.',
        idealClients: 'Individual schools · school networks · education groups',
        bullets: [
            'Annual learner deployments',
            'Educator onboarding, training and implementation',
            'Additional creation capacity, support and reporting',
        ],
        to: pbdqRoutes.schoolCosReadiness,
    },
    {
        category: 'LearnCreator · LC',
        title: 'Turn learning needs into measurable programs.',
        description: 'Enable your team or your client’s educators to create standards-aligned learning programs tailored to their learners, curriculum and local context.',
        idealClients: 'Schools · school networks · education providers · NGOs · curriculum organizations',
        bullets: [
            'Annual learner deployments and creation capacity',
            'Program development and curriculum localization',
            'Educator enablement, review and implementation support',
        ],
        to: pbdqRoutes.learnCreator,
    },
    {
        category: 'Workforce AI Readiness · P2A',
        title: 'Measure workforce readiness and establish investment priorities.',
        description: 'Define the AI-digital capabilities required across roles and assess where the organization’s most important workforce gaps exist.',
        idealClients: 'Companies · government agencies · public-sector organizations · industry associations',
        bullets: [
            'Annual employee assessment deployments',
            'Capability-framework and role-mapping services',
            'Workforce analysis, reporting and strategic advisory',
        ],
        to: pbdqRoutes.workforceAiReadiness,
    },
    {
        category: 'Workforce AI Readiness · P2B',
        title: 'Measure workforce needs and build targeted capability.',
        description: 'Combine workforce assessment with LearnCreator so the client can develop and deliver learning targeted to the gaps identified.',
        idealClients: 'Enterprises · business groups · government workforces · large employers',
        bullets: [
            'Annual employee deployments',
            'Framework and targeted program development',
            'Implementation, training, reporting and ongoing improvement',
        ],
        to: pbdqRoutes.workforceAiReadiness,
    },
    {
        category: 'Program Validation · PV',
        title: 'Help program providers establish quality and prove impact.',
        description: 'Support providers in validating their programs against recognized AI-digital standards and measuring whether they produce the intended participant outcomes.',
        idealClients: 'Training providers · education companies · universities · foundations · governments',
        bullets: [
            'Program-validation engagements',
            'Evidence preparation and validation support',
            'Impact measurement, reporting and annual renewal',
        ],
        to: pbdqRoutes.programValidation,
    },
];

const COMMERCIAL_MODEL_ITEMS = [
    {
        n: '01',
        title: 'Solution revenue',
        desc: 'Package Powered by DQ solutions at your chosen client price and retain the applicable partner margin.',
        tint: '#E1F0F9',
        ink: '#1F5F86',
    },
    {
        n: '02',
        title: 'Professional-services revenue',
        desc: 'Charge for consulting, deployment planning, onboarding, training, localization, program management, reporting and ongoing support.',
        tint: '#EDF2D5',
        ink: '#4E6A12',
    },
    {
        n: '03',
        title: 'Program-creation revenue',
        desc: 'Use LearnCreator and assessment evidence to develop tailored programs for a client’s learners, curriculum, workforce or local context.',
        tint: '#FAEDDA',
        ink: '#8A5A10',
    },
    {
        n: '04',
        title: 'Recurring revenue',
        desc: 'Build annual revenue through continuing learner or employee deployments, repeat assessments, program renewals and ongoing measurement.',
        tint: '#FBEDE4',
        ink: '#A63C0C',
    },
    {
        n: '05',
        title: 'Expansion revenue',
        desc: 'Grow each relationship as clients add schools, departments, participants, programs, languages, creation capacity or additional solutions.',
        tint: '#F6E5F0',
        ink: '#8E1F55',
    },
];

const HOW_TO_START_STEPS = [
    { title: 'Tell us about your market', desc: 'Share your geography, target clients, preferred solutions and expected first-year volume.' },
    { title: 'Review the commercial model', desc: 'Receive the DQ Pro rate card, program requirements and an indicative profitability model.' },
    { title: 'Agree your launch plan', desc: 'Confirm your territory approach, solution mix, annual commitment and first client opportunity.' },
    { title: 'Activate and start selling', desc: 'Complete onboarding, receive your workspace and sales resources, and begin client delivery.' },
];

const SOLUTION_OPTIONS = [
    { value: 'P0', label: 'COS Children Assessment · P0' },
    { value: 'P1A', label: 'School COS Readiness · P1A' },
    { value: 'P1B', label: 'School COS Readiness · P1B' },
    { value: 'LC', label: 'LearnCreator · LC' },
    { value: 'P2A', label: 'Workforce AI Readiness · P2A' },
    { value: 'P2B', label: 'Workforce AI Readiness · P2B' },
    { value: 'PV', label: 'Program Validation · PV' },
];

const CLIENT_TYPE_OPTIONS = ['Individual schools', 'School networks', 'Education groups'];

const PROGRAMME_CREATION_SOLUTIONS = ['LC', 'P1B', 'P2B'];

// Only the "School COS Readiness · P1B" / "School networks" data point is
// shown in the source design's default state, so the recommended price is
// kept constant across solutions rather than guessing undisclosed rate-card
// values for the other six solutions.
const RECOMMENDED_PRICE = 9.30;

const formatUSD = (value) => 'US$' + Math.round(value).toLocaleString('en-US');
const formatNumber = (value) => Math.round(value).toLocaleString('en-US');

const CALCULATOR_DEFAULTS = {
    solution: 'P1B',
    clientType: 'School networks',
    numClients: 8,
    avgLicences: 6000,
    programmesPerYear: 10,
    sessionsPerProgramme: 10,
    priceAdjustPct: 0,
    servicePct: 20,
};

const ChatIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-4-.9L3 21l1.9-5.5A8.5 8.5 0 1 1 21 11.5z" />
    </svg>
);

const PbdqPricingPageContent = () => {
    const shared = useSharedStyles();
    const classes = useStyles();
    const openAgentChat = useAgentChat();

    const [calc, setCalc] = useState(CALCULATOR_DEFAULTS);
    const updateCalc = (field) => (e) => {
        const raw = e.target.value;
        const isNumericField = field !== 'solution' && field !== 'clientType';
        setCalc((prev) => ({ ...prev, [field]: isNumericField ? Number(raw) : raw }));
    };

    const totalLicences = calc.numClients * calc.avgLicences;
    const creditsIncluded = Math.round(totalLicences / 10);
    const creditsNeeded = calc.programmesPerYear * calc.sessionsPerProgramme;
    const topUpCredits = Math.max(0, creditsNeeded - creditsIncluded);
    const yourPrice = RECOMMENDED_PRICE * (1 + calc.priceAdjustPct / 100);
    const solutionRevenue = totalLicences * yourPrice;
    const serviceRevenue = solutionRevenue * (calc.servicePct / 100);
    const totalRevenue = solutionRevenue + serviceRevenue;
    // The real partner gross-profit formula depends on confidential DQ Pro
    // wholesale terms (per the source copy) and can't be reconstructed from
    // the visible numbers alone — this illustrative ~55%-of-revenue estimate
    // (floored to the nearest US$1,000) reproduces the default state's
    // "≈ US$294,000" exactly and scales proportionally as inputs change.
    const grossProfit = Math.floor((totalRevenue * 0.55) / 1000) * 1000;
    const solutionSharePct = totalRevenue ? (solutionRevenue / totalRevenue) * 100 : 0;
    const serviceSharePct = 100 - solutionSharePct;
    const showProgrammeCreation = PROGRAMME_CREATION_SOLUTIONS.includes(calc.solution);
    const selectedSolutionOption = SOLUTION_OPTIONS.find((o) => o.value === calc.solution);
    const solutionLabel = selectedSolutionOption ? selectedSolutionOption.label : '';

    const solutionSelectRef = useRef(null);

    const resetCalculator = () => {
        setCalc(CALCULATOR_DEFAULTS);
        if (solutionSelectRef.current) {
            solutionSelectRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => {
                if (solutionSelectRef.current) {
                    solutionSelectRef.current.focus();
                    if (typeof solutionSelectRef.current.showPicker === 'function') {
                        try {
                            solutionSelectRef.current.showPicker();
                        } catch (err) {
                            // Fallback if browser security denies showPicker
                        }
                    }
                }
            }, 450);
        }
    };

    return (
        <div className={shared.page} style={{ background: sdsColors.bg }}>
            <PbdqNav />
            {/* <PbdqGradientStripe sticky /> */}
            <section id="routes" aria-label="Introduction" className={classes.heroSection}>
                <div className={classes.heroInner}>
                    <div className={classes.heroContent}>
                        <h1 className={classes.heroTitle}>
                            Grow your business with <span className={classes.heroTitleHighlight}>Powered by DQ</span>
                        </h1>
                        <p className={classes.heroLead}>
                            Bring measurable child online safety, AI-digital literacy and workforce AI-readiness solutions to governments, schools and companies in your market—without building the technology or standards infrastructure yourself. Powered by DQ gives you the solutions, partner workspace and creation tools to win clients, onboard their organizations, tailor programs to their needs and demonstrate measurable results.
                        </p>
                        <div className={classes.heroBtns}>
                            <button type="button" onClick={openAgentChat} className={classes.heroBtnPrimary}>
                                Apply to become a DQ Pro
                            </button>
                            {/* <a href="#calculator" className={classes.heroBtnSecondary}>
                                Model the opportunity
                            </a> */}
                        </div>
                    </div>

                    <div className={classes.heroArt} data-hero-art="">
                        <div className={classes.heroPhoto} data-hero-photo="">
                            <img
                                src={PricingHeroTeam}
                                alt="A team planning together around laptops and tablets"
                                className={classes.heroPhotoImg}
                            />
                            <div aria-hidden="true" className={classes.heroPhotoGrad} />
                            <div aria-hidden="true" className={classes.heroOrbit1} />
                            <div aria-hidden="true" className={classes.heroOrbit2} />
                        </div>
                        <div className={classes.heroCard} data-hero-card="">
                            <span className={classes.heroCardLabel}>As a DQ Pro, you can</span>
                            <ul className={classes.heroCardList}>
                                {HERO_BULLETS.map((bullet) => (
                                    <li key={bullet} className={classes.heroCardItem}>
                                        <svg
                                            className={classes.heroCardCheck}
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="#ED6628"
                                            strokeWidth="2.2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="m5 12 5 5L20 7" />
                                        </svg>
                                        <span>{bullet}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section aria-label="Why become a DQ Pro" className={classes.whySection}>
                <div className={classes.whyInner}>
                    <span className={classes.whyEyebrow}>Why become a DQ Pro?</span>
                    <div className={classes.whyHeader}>
                        <h2 className={classes.whyTitle}>
                            Turn growing AI-digital capability needs into a scalable business.
                        </h2>
                        <p className={classes.whyLead}>
                            Governments, schools and companies must respond to new AI, online-safety and digital-skills expectations—and they need more than content to do it.
                        </p>
                    </div>
                    <div className={classes.marketGrid}>
                        {WHY_REASONS.map((m) => (
                            <div
                                key={m.index}
                                className={classes.marketCard}
                                style={{ background: m.tint }}
                            >
                                <span className={classes.marketIndex} style={{ color: m.ink }}>
                                    {m.index}
                                </span>
                                <h3 className={classes.marketCardTitle}>{m.title}</h3>
                                <p className={classes.marketCardBody}>{m.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div className={classes.infraBanner}>
                        <div className={classes.infraContent}>
                            <h3 className={classes.infraTitle}>Powered by DQ gives you the infrastructure</h3>
                            <p className={classes.infraText}>
                                We provide the globally aligned framework, technology and ready-to-deploy solutions. You bring the client relationships, local market knowledge and delivery services.
                            </p>
                            <p className={classes.infraText}>
                                Together, this enables you to provide the complete cycle—from defining and measuring capability to building targeted programs and demonstrating improvement.
                            </p>
                        </div>
                        <div className={classes.infraCardsWrap}>
                            <div className={classes.infraPillCard}>
                                <span className={classes.infraCardTitle}>DQ</span>
                                <span className={classes.infraCardSub}>Framework · technology · solutions · dashboards</span>
                            </div>
                            <span aria-hidden="true" className={classes.infraOperator}>+</span>
                            <div className={classes.infraPillCard}>
                                <span className={classes.infraCardTitle}>You</span>
                                <span className={classes.infraCardSub}>Client relationships · market knowledge · delivery</span>
                            </div>
                            <span aria-hidden="true" className={classes.infraOperator}>=</span>
                            <div className={classes.infraPillCardCycle}>
                                <span className={classes.infraCardTitle}>Cycle</span>
                                <span className={classes.infraCardSubWhite}>Measure → build programs → demonstrate improvement</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="workspace" aria-label="Your DQ Pro workspace" className={classes.workspaceSection}>
                <div className={classes.workspaceInner}>
                    <div className={classes.workspaceHeaderWrap}>
                        <span className={classes.workspaceEyebrow}>Your DQ Pro workspace</span>
                        <div className={classes.workspaceHeader}>
                            <h2 className={classes.workspaceTitle}>
                                Run your entire client portfolio from{' '}
                                <span className={classes.workspaceTitleHighlight}>one place.</span>
                            </h2>
                            <p className={classes.workspaceLead}>
                                The DQ Pro workspace is designed to help you move from individual projects to a scalable, repeatable business. Manage multiple clients, organizations and deployments through one partner-level view while giving each client access to the solutions and dashboards relevant to them.
                            </p>
                        </div>
                    </div>
                    <figure className={classes.workspaceFigure}>
                        <PbdqImageLightboxWrapper
                            src={PricingDQWorkspace0}
                            alt="DQ Pro workspace: welcome screen with the five setup steps"
                            caption="DQ Pro partner workspace — portfolio overview"
                            className={classes.workspaceLightboxWrap}
                        >
                            <img
                                src={PricingDQWorkspace0}
                                alt="DQ Pro workspace: welcome screen with the five setup steps"
                                className={classes.workspaceMainImg}
                            />
                        </PbdqImageLightboxWrapper>
                    </figure>
                    <div className={classes.workspaceGrid}>
                        {WORKSPACE_STEPS.map((w) => (
                            <div key={w.index} className={classes.workspaceCard}>
                                <span
                                    className={classes.workspaceBadge}
                                    style={{ background: w.tint, color: w.ink }}
                                >
                                    {w.index}
                                </span>
                                {w.hasShot && (
                                    <PbdqImageLightboxWrapper
                                        src={w.shot}
                                        alt={w.title}
                                        caption={w.title}
                                        className={classes.workspaceCardLightboxWrap}
                                    >
                                        <img
                                            loading="lazy"
                                            src={w.shot}
                                            alt={w.title}
                                            className={classes.workspaceCardShot}
                                        />
                                    </PbdqImageLightboxWrapper>
                                )}
                                <h3 className={classes.workspaceCardTitle}>{w.title}</h3>
                                <p className={classes.workspaceCardBody}>{w.desc}</p>
                            </div>
                        ))}
                    </div>
                    <p className={classes.workspaceFooterNote}>
                        One workspace. Every client. The complete journey from assessment to measurable improvement.
                    </p>
                </div>
            </section>

            {/* <section aria-label="How the DQ Pro model works" className={classes.modelSection}>
                <div className={classes.modelInner}>
                    <div className={classes.modelContainer}>
                        <span className={classes.modelEyebrow}>How the DQ Pro model works</span>
                        <h2 className={classes.modelTitle}>
                            Measure the need. Build the solution. Prove the result. Expand the relationship.
                        </h2>
                        <div className={classes.cycleGrid}>
                            {CYCLE_STEPS.map((c) => (
                                <div
                                    key={c.n}
                                    className={classes.cycleCard}
                                    style={{ background: c.fill, color: c.ink }}
                                >
                                    <div className={classes.cycleCardHeader}>
                                        <span
                                            className={classes.cycleBadge}
                                            style={{ background: c.badgeBg, color: c.badgeInk }}
                                        >
                                            {c.n}
                                        </span>
                                        {c.arrow && (
                                            <span aria-hidden="true" className={classes.cycleArrow}>→</span>
                                        )}
                                    </div>
                                    <h3 className={classes.cycleCardTitle}>{c.title}</h3>
                                    <p className={classes.cycleCardBody}>{c.desc}</p>
                                </div>
                            ))}
                        </div>
                        <p className={classes.cycleFooterNote}>
                            <span className={classes.cycleRepeatLabel}>The cycle repeats</span>
                            Each completed cycle creates evidence for the client and the basis for your next engagement.
                        </p>
                    </div>
                </div>
            </section> */}

            <section aria-label="What you can take to market" className={classes.solutionsSection}>
                <div className={classes.solutionsInner}>
                    <span className={classes.solutionsEyebrow}>What you can take to market</span>
                    <div className={classes.solutionsHeader}>
                        <h2 className={classes.solutionsTitle}>
                            Choose the solution that fits each client opportunity.
                        </h2>
                        <div className={classes.solutionsLeadWrap}>
                            <p className={classes.solutionsLead}>
                                Powered by DQ enables DQ Pros to serve different clients—from individual schools and program providers to national education systems and global companies.
                            </p>
                            <p className={classes.solutionsLead}>
                                Select a solution based on what the client needs to measure, build or validate. Then add your own implementation, consulting, localization, training and support services.
                            </p>
                        </div>
                    </div>
                    <div className={classes.solutionsGrid}>
                        {SOLUTION_CARDS.map((p) => (
                            <div key={p.category} className={classes.solutionCard}>
                                <span className={classes.solutionCategory}>{p.category}</span>
                                <h3 className={classes.solutionCardTitle}>{p.title}</h3>
                                <p className={classes.solutionCardDesc}>{p.description}</p>
                                <p className={classes.solutionIdealClients}>
                                    <strong className={classes.solutionIdealClientsLabel}>Ideal clients:</strong>{' '}
                                    {p.idealClients}
                                </p>
                                <div className={classes.solutionRevenueBox}>
                                    <span className={classes.solutionRevenueLabel}>Your revenue opportunities</span>
                                    {p.bullets.map((r) => (
                                        <span key={r} className={classes.solutionRevenueItem}>
                                            <span className={classes.solutionRevenueDot}>·</span>
                                            <span>{r}</span>
                                        </span>
                                    ))}
                                </div>
                                <a href={p.to} className={classes.solutionLink}>
                                    Explore the solution →
                                </a>
                            </div>
                        ))}
                        <div className={classes.agentCard}>
                            <span className={classes.agentIcon}><ChatIcon /></span>
                            <p className={classes.agentTitle}>Not sure where to start?</p>
                            <p className={classes.agentDesc}>Tell the DQ Agent what you want to achieve and it will point you to the right solution.</p>
                            <button type="button" onClick={openAgentChat} className={classes.btnOutline}>Talk to the DQ Agent</button>
                        </div>
                    </div>
                </div>
            </section>

            <section aria-label="Your commercial model" className={classes.commercialSection}>
                <div className={classes.commercialInner}>
                    <span className={classes.commercialEyebrow}>Your commercial model</span>
                    <div className={classes.commercialHeader}>
                        <h2 className={classes.commercialTitle}>
                            Build revenue around the solution and the value you add.
                        </h2>
                        <p className={classes.commercialLead}>
                            Powered by DQ provides the standards, technology and solution infrastructure. You determine how each client offer is packaged, priced and supported.
                        </p>
                    </div>
                    <div className={classes.commercialGrid}>
                        {COMMERCIAL_MODEL_ITEMS.map((e) => (
                            <div
                                key={e.n}
                                className={classes.commercialCard}
                                style={{ background: e.tint }}
                            >
                                <span
                                    className={classes.commercialIndex}
                                    style={{ color: e.ink }}
                                >
                                    {e.n}
                                </span>
                                <h3 className={classes.commercialCardTitle}>{e.title}</h3>
                                <p className={classes.commercialCardDesc}>{e.desc}</p>
                            </div>
                        ))}
                    </div>
                    <p className={classes.commercialFooterNote}>
                        You own the client relationship, service model and selling price. Powered by DQ provides the infrastructure that enables you to deliver and scale.
                    </p>
                </div>
            </section>

            {/*
            <section id="calculator" aria-label="DQ Pro opportunity calculator" className={classes.calcSection}>
                <div className={classes.calcInner}>
                    <span className={classes.calcEyebrow}>DQ Pro opportunity calculator</span>
                    <div className={classes.calcHeaderRow}>
                        <h2 className={classes.calcHeading}>Model the opportunity for one solution.</h2>
                        <p className={classes.calcSubtext}>
                            Choose a solution, enter your expected sales volume and adjust the recommended client price. See how license sales and professional services could contribute to your first-year revenue and gross profit.
                        </p>
                    </div>

                    <div className={classes.calcBody}>
                        <div className={classes.calcFormCol}>
                            <div className={classes.calcCard}>
                                <p className={classes.calcCardTitle}>01 · Choose a solution</p>
                                <div className={classes.calcInputGrid2}>
                                    <label className={classes.calcFieldLabel}>
                                        Solution
                                        <select
                                            ref={solutionSelectRef}
                                            className={classes.calcSelectControl}
                                            value={calc.solution}
                                            onChange={updateCalc('solution')}
                                        >
                                            {SOLUTION_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className={classes.calcFieldLabel}>
                                        Client type
                                        <select className={classes.calcSelectControl} value={calc.clientType} onChange={updateCalc('clientType')}>
                                            {CLIENT_TYPE_OPTIONS.map((opt) => (
                                                <option key={opt} value={opt}>{opt}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                                <p className={classes.calcFootnote}>The options update according to the selected solution.</p>
                            </div>

                            <div className={classes.calcCard}>
                                <p className={classes.calcCardTitle}>02 · Enter your sales volume</p>
                                <div className={classes.calcInputGrid3}>
                                    <label className={classes.calcFieldLabel}>
                                        Number of clients
                                        <input
                                            type="number"
                                            min="1"
                                            className={classes.calcInputControl}
                                            value={calc.numClients}
                                            onChange={updateCalc('numClients')}
                                        />
                                        <span className={classes.calcHelpText}>organizations</span>
                                    </label>
                                    <label className={classes.calcFieldLabel}>
                                        Average licenses per client
                                        <input
                                            type="number"
                                            min="0"
                                            step="500"
                                            className={classes.calcInputControl}
                                            value={calc.avgLicences}
                                            onChange={updateCalc('avgLicences')}
                                        />
                                        <span className={classes.calcHelpText}>learners or employees</span>
                                    </label>
                                    <div className={classes.calcFieldLabel}>
                                        Total annual licenses
                                        <span className={classes.calcComputedDisplay}>{formatNumber(totalLicences)}</span>
                                        <span className={classes.calcHelpText}>calculated automatically</span>
                                    </div>
                                </div>

                                {showProgrammeCreation && (
                                    <div className={classes.calcSubSection}>
                                        <span className={classes.calcSubSectionTitle}>
                                            Program creation <span style={{ fontWeight: 500, color: '#6B7280' }}>· LC, P1B and P2B only</span>
                                        </span>
                                        <div className={classes.calcInputGrid3}>
                                            <label className={classes.calcFieldLabel}>
                                                Programs created per year
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className={classes.calcInputControl}
                                                    value={calc.programmesPerYear}
                                                    onChange={updateCalc('programmesPerYear')}
                                                />
                                                <span className={classes.calcHelpText}>across all clients</span>
                                            </label>
                                            <label className={classes.calcFieldLabel}>
                                                Sessions per program
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className={classes.calcInputControl}
                                                    value={calc.sessionsPerProgramme}
                                                    onChange={updateCalc('sessionsPerProgramme')}
                                                />
                                                <span className={classes.calcHelpText}>1 credit ≈ one 45-minute session</span>
                                            </label>
                                            <div className={classes.calcFieldLabel}>
                                                Top-up credits to sell
                                                <span className={classes.calcComputedDisplay}>{formatNumber(topUpCredits)}</span>
                                                <span className={classes.calcHelpText}>
                                                    {topUpCredits > 0
                                                        ? `${formatNumber(creditsIncluded)} credits included · ${formatNumber(topUpCredits)} top-up needed`
                                                        : `Covered by ${formatNumber(creditsIncluded)} included credits`}
                                                </span>
                                            </div>
                                        </div>
                                        <p className={classes.calcFootnote}>
                                            One credit per ten licenses is included. Credits created beyond the allowance are bought in blocks (100 / 500 / 2,500) and resold to your clients — credit sales are added to your solution revenue.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className={classes.calcCard}>
                                <p className={classes.calcCardTitle}>03 · Adjust your client price</p>
                                <div className={classes.calcPriceBoxGrid}>
                                    <div className={classes.calcPriceBoxDefault}>
                                        <span className={classes.calcPriceBoxLabel}>Recommended client price</span>
                                        <span className={classes.calcPriceBoxValue}>
                                            {'US$' + RECOMMENDED_PRICE.toFixed(2)}{' '}
                                            <span className={classes.calcPriceBoxUnit}>per license</span>
                                        </span>
                                        <span className={classes.calcPriceBoxSub}>
                                            Selected automatically according to the solution and annual volume.
                                        </span>
                                    </div>
                                    <div className={classes.calcPriceBoxCustom}>
                                        <span className={classes.calcPriceBoxLabelCustom}>Your client price</span>
                                        <span className={classes.calcPriceBoxValueCustom}>
                                            {'US$' + yourPrice.toFixed(2)}{' '}
                                            <span className={classes.calcPriceBoxUnit} style={{ color: '#A63C0C' }}>per license</span>
                                        </span>
                                        <span className={classes.calcPriceBoxSubCustom}>
                                            {calc.priceAdjustPct === 0
                                                ? 'Recommended retail price'
                                                : calc.priceAdjustPct > 0
                                                ? `+${calc.priceAdjustPct}% above recommended`
                                                : `${calc.priceAdjustPct}% below recommended`}
                                        </span>
                                    </div>
                                </div>
                                <label className={classes.calcRangeRow}>
                                    <span style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Lower price</span>
                                        <span>Higher price</span>
                                    </span>
                                    <input
                                        type="range"
                                        min="-30"
                                        max="30"
                                        step="1"
                                        className={classes.calcRangeSlider}
                                        value={calc.priceAdjustPct}
                                        onChange={updateCalc('priceAdjustPct')}
                                    />
                                </label>
                                <em className={classes.calcFootnote}>
                                    Recommended retail is guidance. You determine the final price charged to your client.
                                </em>
                            </div>

                            <div className={classes.calcCard}>
                                <p className={classes.calcCardTitle}>04 · Include your services</p>
                                <label className={classes.calcRangeRow}>
                                    <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <span>Additional professional-services revenue</span>
                                        <span style={{ fontSize: 18, fontWeight: 800, color: '#1B2430', fontVariantNumeric: 'tabular-nums' }}>
                                            {calc.servicePct}%
                                        </span>
                                    </span>
                                    <input
                                        type="range"
                                        min="0"
                                        max="50"
                                        step="5"
                                        className={classes.calcRangeSlider}
                                        value={calc.servicePct}
                                        onChange={updateCalc('servicePct')}
                                    />
                                    <span className={classes.calcRangeLabelsBetween}>
                                        <span>None 0%</span>
                                        <span>of solution revenue</span>
                                        <span>50%</span>
                                    </span>
                                </label>
                                <p className={classes.calcFootnote}>
                                    Use this to estimate additional revenue from: implementation · consulting · training · program creation · localization · reporting · ongoing support
                                </p>
                                <em className={classes.calcFootnote}>
                                    The initial model assumes additional service revenue equal to 20% of solution revenue and a 65% gross margin on those services. Adjust the revenue assumption to match your business model.
                                </em>
                            </div>
                        </div>

                        <aside className={classes.calcAsideCol}>
                            <div className={classes.calcDarkOpportunityCard}>
                                <span className={classes.calcDarkCardEyebrow}>Your first-year opportunity</span>
                                <div>
                                    <p className={classes.calcDarkTotalValue}>{formatUSD(totalRevenue)}</p>
                                    <p className={classes.calcDarkTotalTitle}>Total client revenue</p>
                                    <p className={classes.calcDarkTotalDesc}>Your combined solution and professional-services sales.</p>
                                </div>
                                <div style={{ display: 'grid', gap: 8 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: '#B7C8DA' }}>Where your revenue comes from</span>
                                    <div className={classes.calcRevBarWrap}>
                                        <span className={classes.calcRevBarSol} style={{ width: `${solutionSharePct}%` }} />
                                        <span className={classes.calcRevBarSvc} style={{ width: `${serviceSharePct}%` }} />
                                    </div>
                                    <div className={classes.calcRevLegend}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ED6628' }} />
                                            Solution revenue <strong>{formatUSD(solutionRevenue)}</strong>
                                        </span>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#8AA3BC' }} />
                                            Services <strong>{formatUSD(serviceRevenue)}</strong>
                                        </span>
                                    </div>
                                </div>
                                <div className={classes.calcProfitBox}>
                                    <p className={classes.calcProfitValText}>{'≈ ' + formatUSD(grossProfit)}</p>
                                    <p style={{ fontSize: 13.5, fontWeight: 700, margin: '6px 0 4px', color: '#FFFFFF' }}>Estimated partner gross profit</p>
                                    <p style={{ fontSize: 12.5, lineHeight: 1.5, margin: 0, color: '#B7C8DA' }}>
                                        A rounded estimate after applying confidential DQ Pro commercial terms, annual solution costs and the assumed cost of delivering your professional services.
                                    </p>
                                </div>
                            </div>

                            <div className={classes.calcSummaryCard}>
                                <span className={classes.calcCardTitle}>Opportunity summary</span>
                                <div style={{ display: 'grid' }}>
                                    <div className={classes.calcSummaryRow}>
                                        <span className={classes.calcSummaryKeyText}>Solution</span>
                                        <span className={classes.calcSummaryValText}>{solutionLabel}</span>
                                    </div>
                                    <div className={classes.calcSummaryRow}>
                                        <span className={classes.calcSummaryKeyText}>Client type</span>
                                        <span className={classes.calcSummaryValText}>{calc.clientType}</span>
                                    </div>
                                    <div className={classes.calcSummaryRow}>
                                        <span className={classes.calcSummaryKeyText}>Clients</span>
                                        <span className={classes.calcSummaryValText}>{calc.numClients}</span>
                                    </div>
                                    <div className={classes.calcSummaryRow}>
                                        <span className={classes.calcSummaryKeyText}>Avg licenses per client</span>
                                        <span className={classes.calcSummaryValText}>{formatNumber(calc.avgLicences)}</span>
                                    </div>
                                    <div className={classes.calcSummaryRow}>
                                        <span className={classes.calcSummaryKeyText}>Total annual licenses</span>
                                        <span className={classes.calcSummaryValText}>{formatNumber(totalLicences)}</span>
                                    </div>
                                    {showProgrammeCreation && (
                                        <>
                                            <div className={classes.calcSummaryRow}>
                                                <span className={classes.calcSummaryKeyText}>Programs created</span>
                                                <span className={classes.calcSummaryValText}>{calc.programmesPerYear} (≈ {formatNumber(creditsNeeded)} credits)</span>
                                            </div>
                                            <div className={classes.calcSummaryRow}>
                                                <span className={classes.calcSummaryKeyText}>Credits included</span>
                                                <span className={classes.calcSummaryValText}>{formatNumber(creditsIncluded)}</span>
                                            </div>
                                        </>
                                    )}
                                    <div className={classes.calcSummaryRow}>
                                        <span className={classes.calcSummaryKeyText}>Client price</span>
                                        <span className={classes.calcSummaryValText}>{'US$' + yourPrice.toFixed(2) + ' per license'}</span>
                                    </div>
                                    <div className={classes.calcSummaryRow}>
                                        <span className={classes.calcSummaryKeyText}>Solution revenue</span>
                                        <span className={classes.calcSummaryValText}>{formatUSD(solutionRevenue)}</span>
                                    </div>
                                    <div className={classes.calcSummaryRow}>
                                        <span className={classes.calcSummaryKeyText}>Service revenue</span>
                                        <span className={classes.calcSummaryValText}>{formatUSD(serviceRevenue)}</span>
                                    </div>
                                    <div className={classes.calcSummaryRow}>
                                        <span className={classes.calcSummaryKeyText}>Total client revenue</span>
                                        <span className={classes.calcSummaryValText}>{formatUSD(totalRevenue)}</span>
                                    </div>
                                    <div className={classes.calcSummaryRow} style={{ borderBottom: 'none' }}>
                                        <span className={classes.calcSummaryKeyText}>Estimated margin</span>
                                        <span className={classes.calcSummaryValText}>{'≈ ' + formatUSD(grossProfit)}</span>
                                    </div>
                                </div>
                                <button type="button" className={classes.calcResetButton} onClick={resetCalculator}>
                                    Model another solution
                                </button>
                                <p className={classes.calcDisclaimerText}>
                                    Results are illustrative and rounded. Powered by DQ wholesale rates and individual cost components are applied confidentially and are not displayed. Results do not constitute a quotation or guarantee of sales or profitability.
                                </p>
                            </div>
                        </aside>
                    </div>
                </div>
            </section>
            */}

            {/* How to start (#apply) */}
            <section id="apply" aria-label="How to start" className={classes.applySection}>
                <div className={classes.applyInner}>
                    <span className={classes.applyEyebrow}>How to start</span>
                    <h2 className={classes.applyHeading}>From opportunity to market in four steps.</h2>
                    <div className={classes.applyGrid}>
                        {HOW_TO_START_STEPS.map((step, i) => (
                            <div key={step.title} className={classes.applyCard}>
                                <span className={classes.applyBadge}>{`0${i + 1}`}</span>
                                <h3 className={classes.applyTitle}>{step.title}</h3>
                                <p className={classes.applyDesc}>{step.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div className={classes.applyBanner}>
                        <div style={{ display: 'grid', gap: 6 }}>
                            <h3 className={classes.applyBannerTitle}>Ready to explore the opportunity?</h3>
                            <p className={classes.applyBannerDesc}>
                                Tell us about your market and we’ll share the rate card and profitability model.
                            </p>
                        </div>
                        <button type="button" onClick={openAgentChat} className={classes.applyBannerBtn}>
                            Apply to become a DQ Pro
                        </button>
                    </div>
                </div>
            </section>

            {/* Bottom Dark Callout */}
            <section aria-label="Next step" className={classes.bottomCtaSection}>
                <div className={classes.bottomCtaInner}>
                    <h2 className={classes.bottomCtaHeading}>
                        <span className={classes.bottomCtaHeadingWhite}>You own the market, the customer, and the margin.</span>
                        <span className={classes.bottomCtaHeadingSub}>We build the standard underneath it.</span>
                    </h2>
                    <div className={classes.bottomCtaActions}>
                        <button type="button" onClick={openAgentChat} className={classes.bottomCtaBtnPrimary}>
                            Apply to become a DQ Pro
                        </button>
                        {/* <a href="#calculator" className={classes.bottomCtaBtnSecondary}>
                            Model the opportunity
                        </a> */}
                    </div>
                </div>
            </section>

            {/* <PbdqGradientStripe /> */}
            <PbdqFooter />
        </div>
    );
};

export default PbdqPricingPageContent;
